// Stardust WebGL2 renderer: PBR-lite forward shading, soft shadows, planar
// reflections, HDR + physically-inspired bloom, ACES tonemapping and grading.
import { mat4, col } from './math.js';
import { STRIDE } from './geometry.js';
import * as S from './shaders.js';

// ---------------------------------------------------------------- scene graph
export class Mesh {
  constructor(gl, builder) {
    this.gl = gl;
    this.count = builder.i.length;
    // kept, so the mesh can be sent to the GPU again if the WebGL context is lost and restored
    this.v = new Float32Array(builder.v);
    this.i = new Uint32Array(builder.i);
    Mesh.all.add(this);
    this._upload();
  }
  _upload() {
    const gl = this.gl;
    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);
    this.vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.v, gl.STATIC_DRAW);
    const F = 4, st = STRIDE * F;
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, st, 0);
    gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 3, gl.FLOAT, false, st, 3 * F);
    gl.enableVertexAttribArray(2); gl.vertexAttribPointer(2, 3, gl.FLOAT, false, st, 6 * F);
    gl.enableVertexAttribArray(3); gl.vertexAttribPointer(3, 4, gl.FLOAT, false, st, 9 * F);
    this.ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.i, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
  }
  dispose() {
    const gl = this.gl;
    Mesh.all.delete(this);
    gl.deleteBuffer(this.vbo);
    gl.deleteBuffer(this.ibo);
    gl.deleteVertexArray(this.vao);
  }
}

/** Every live mesh (re-uploaded after a lost WebGL context comes back). */
Mesh.all = new Set();

const NO_BEAM = new Float32Array(4);
export class Node {
  constructor(mesh = null, opts = {}) {
    this.mesh = mesh;
    this.position = [0, 0, 0];
    this.rotation = [0, 0, 0];
    this.scale = [1, 1, 1];
    this.children = [];
    this.parent = null;
    this.visible = true;
    this.tint = [1, 1, 1, 1];
    this.emissive = [0, 0, 0];
    this.emitMul = 1;
    this.castShadow = opts.castShadow ?? true;
    this.fx = opts.fx ?? null; // null | {mode, param}
    this.reflect = opts.reflect ?? true; // drawn in reflection pass
    this.local = mat4.create();
    this.world = mat4.create();
    this.name = opts.name || '';
    this.userData = {};
  }
  add(...nodes) {
    for (const n of nodes) {
      if (n.parent) n.parent.remove(n);
      n.parent = this;
      this.children.push(n);
    }
    return this;
  }
  remove(n) {
    const i = this.children.indexOf(n);
    if (i >= 0) this.children.splice(i, 1);
    n.parent = null;
  }
  setPos(x, y, z) {
    this.position[0] = x; this.position[1] = y; this.position[2] = z;
    return this;
  }
  setScale(s) {
    if (typeof s === 'number') this.scale = [s, s, s];
    else this.scale = s.slice();
    return this;
  }
  updateWorld(parentWorld) {
    mat4.compose(this.local, this.position, this.rotation, this.scale);
    if (parentWorld) mat4.multiply(this.world, parentWorld, this.local);
    else this.world.set(this.local);
    for (const c of this.children) c.updateWorld(this.world);
  }
  worldPos(out = [0, 0, 0]) {
    out[0] = this.world[12]; out[1] = this.world[13]; out[2] = this.world[14];
    return out;
  }
  traverse(fn) {
    fn(this);
    for (const c of this.children) c.traverse(fn);
  }
}

export class Light {
  constructor(opts = {}) {
    this.position = opts.position || [0, 3, 0];
    this.color = opts.color ? (typeof opts.color === 'string' ? col(opts.color) : opts.color) : [1, 1, 1];
    this.intensity = opts.intensity ?? 10;
    this.range = opts.range ?? 8;
    this.dir = opts.dir || null; // spot direction (normalised)
    this.cone = opts.cone ?? 0.5; // radians half-angle
    this.soft = opts.soft ?? 0.15;
    this.enabled = true;
    this.priority = opts.priority ?? 0;
  }
}

export class Camera {
  constructor() {
    this.position = [0, 10, 10];
    this.target = [0, 0, 0];
    this.up = [0, 1, 0];
    this.fov = 40 * (Math.PI / 180);
    this.near = 0.1;
    this.far = 400;
    this.aspect = 1;
    this.view = mat4.create();
    this.proj = mat4.create();
    this.viewProj = mat4.create();
    this.invViewProj = mat4.create();
  }
  update(aspect) {
    this.aspect = aspect;
    mat4.lookAt(this.view, this.position, this.target, this.up);
    mat4.perspective(this.proj, this.fov, aspect, this.near, this.far);
    mat4.multiply(this.viewProj, this.proj, this.view);
    mat4.invert(this.invViewProj, this.viewProj);
  }
  /** world -> screen pixels */
  project(p, w, h, out = { x: 0, y: 0, visible: true }) {
    const m = this.viewProj;
    const x = p[0], y = p[1], z = p[2];
    const cw = m[3] * x + m[7] * y + m[11] * z + m[15];
    const cx = (m[0] * x + m[4] * y + m[8] * z + m[12]) / cw;
    const cy = (m[1] * x + m[5] * y + m[9] * z + m[13]) / cw;
    out.x = (cx * 0.5 + 0.5) * w;
    out.y = (1 - (cy * 0.5 + 0.5)) * h;
    out.visible = cw > 0;
    return out;
  }
}

// ---------------------------------------------------------------- particles
export class ParticleSystem {
  constructor(max = 3000, additive = true) {
    this.max = max;
    this.additive = additive;
    this.n = 0;
    // per particle: pos3, vel3, life, maxLife, size0, size1, color4 (start), color4(end), rot, rotSpeed, type, drag, gravity
    this.p = new Float32Array(max * 24);
    this.inst = new Float32Array(max * 10);
  }
  emit(o) {
    if (this.n >= this.max) return;
    const i = this.n++ * 24, p = this.p;
    p[i] = o.pos[0]; p[i + 1] = o.pos[1]; p[i + 2] = o.pos[2];
    const v = o.vel || [0, 0, 0];
    p[i + 3] = v[0]; p[i + 4] = v[1]; p[i + 5] = v[2];
    p[i + 6] = 0; p[i + 7] = o.life ?? 1;
    p[i + 8] = o.size ?? 0.1; p[i + 9] = o.size1 ?? o.size ?? 0.1;
    const c0 = o.color || [1, 1, 1], c1 = o.color1 || c0;
    p[i + 10] = c0[0]; p[i + 11] = c0[1]; p[i + 12] = c0[2]; p[i + 13] = o.alpha ?? 1;
    p[i + 14] = c1[0]; p[i + 15] = c1[1]; p[i + 16] = c1[2]; p[i + 17] = o.alpha1 ?? 0;
    p[i + 18] = o.rot ?? Math.random() * 6.28; p[i + 19] = o.spin ?? 0;
    p[i + 20] = o.type ?? 0; p[i + 21] = o.drag ?? 0; p[i + 22] = o.gravity ?? 0;
  }
  update(dt) {
    const p = this.p;
    let n = this.n;
    for (let k = 0; k < n; k++) {
      const i = k * 24;
      p[i + 6] += dt;
      if (p[i + 6] >= p[i + 7]) {
        // swap-remove
        n--;
        if (k !== n) p.copyWithin(i, n * 24, n * 24 + 24);
        k--;
        continue;
      }
      const drag = Math.exp(-p[i + 21] * dt);
      p[i + 3] *= drag; p[i + 4] = p[i + 4] * drag - p[i + 22] * dt; p[i + 5] *= drag;
      p[i] += p[i + 3] * dt; p[i + 1] += p[i + 4] * dt; p[i + 2] += p[i + 5] * dt;
      p[i + 18] += p[i + 19] * dt;
    }
    this.n = n;
  }
  fill() {
    const p = this.p, o = this.inst;
    for (let k = 0; k < this.n; k++) {
      const i = k * 24, j = k * 10;
      const t = p[i + 6] / p[i + 7];
      o[j] = p[i]; o[j + 1] = p[i + 1]; o[j + 2] = p[i + 2];
      o[j + 3] = p[i + 8] + (p[i + 9] - p[i + 8]) * t;
      // fade in quickly
      const fin = Math.min(1, t * 8);
      o[j + 4] = p[i + 10] + (p[i + 14] - p[i + 10]) * t;
      o[j + 5] = p[i + 11] + (p[i + 15] - p[i + 11]) * t;
      o[j + 6] = p[i + 12] + (p[i + 16] - p[i + 12]) * t;
      o[j + 7] = (p[i + 13] + (p[i + 17] - p[i + 13]) * t) * fin;
      o[j + 8] = p[i + 18]; o[j + 9] = p[i + 20];
    }
    return this.n;
  }
  clear() {
    this.n = 0;
  }
}

// ---------------------------------------------------------------- scene
export class Scene {
  constructor() {
    this.root = new Node();
    this.lights = [];
    this.particles = [new ParticleSystem(4000, true), new ParticleSystem(1500, false)];
    this.env = {
      sunDir: [-0.4, -1, -0.5],
      sunColor: col('#fff1dc'),
      sunIntensity: 2.5,
      skyAmb: col('#35336a'),
      groundAmb: col('#1a1024'),
      rim: col('#6a7cff'),
      rimIntensity: 0.25,
      fogColor: col('#07061a'),
      fogDensity: 0.0,
      fogHeight: 0,
      envIntensity: 1.0,
      exposure: 1.0,
      bloom: 0.9,
      bloomThreshold: 1.0,
      vignette: 0.35,
      grain: 0.03,
      aberration: 0.012,
      saturation: 1.08,
      lift: [0.0, 0.0, 0.02],
      gain: [1, 1, 1],
      reflections: true,
      shadowCenter: [0, 0, 0],
      shadowRadius: 12,
      clipY: -1e9,
    };
    this.sky = {
      neb1: col('#5b2a86'), neb2: col('#1fb5c9'), neb3: col('#ff6fb5'), space: col('#030312'),
      starDir: [0.6, 0.35, -0.7], starCol: col('#ffd9a8'),
      planetDir: [-0.6, 0.25, -0.75], planetSize: 0.16, planetCol: col('#5f3fa0'), planetCol2: col('#f0a1c8'), ring: 1,
      nebIntensity: 1.0, seed: 1.0,
    };
    this.skyVisible = true;
    this.flash = 0;
    this.flashColor = [1, 1, 1];
    this.desat = 0;
  }
  add(...n) {
    this.root.add(...n);
    return this;
  }
}

// ---------------------------------------------------------------- renderer
function compile(gl, vs, fs, name) {
  const mk = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(s);
      console.error(name, log, src.split('\n').map((l, i) => i + 1 + ': ' + l).join('\n'));
      throw new Error('Shader compile failed: ' + name + '\n' + log);
    }
    return s;
  };
  const p = gl.createProgram();
  gl.attachShader(p, mk(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, mk(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('Link failed ' + name + gl.getProgramInfoLog(p));
  const u = {};
  const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(p, i);
    const nm = info.name.replace(/\[0\]$/, '');
    u[nm] = gl.getUniformLocation(p, info.name);
  }
  return { p, u };
}

export const QUALITY = {
  // low keeps full sharpness (native CSS resolution) and saves time elsewhere: fewer lights, lighter sky, no shadows
  low: { scale: 1, dprMax: 1, shadows: false, shadowSize: 1024, reflections: false, bloom: true, msaa: 0, bloomLevels: 4, maxLights: 8, skyOctaves: 3, minDyn: 0.85 },
  medium: { scale: 1, dprMax: 1.25, shadows: true, shadowSize: 1024, reflections: false, bloom: true, msaa: 0, bloomLevels: 5, maxLights: 14, skyOctaves: 4, minDyn: 0.8 },
  high: { scale: 1, dprMax: 1.25, shadows: true, shadowSize: 2048, reflections: true, bloom: true, msaa: 4, bloomLevels: 6, maxLights: 32, skyOctaves: 5, minDyn: 0.75 },
  ultra: { scale: 1, dprMax: 2, shadows: true, shadowSize: 4096, reflections: true, bloom: true, msaa: 4, bloomLevels: 7, hidpi: true, maxLights: 32, skyOctaves: 5, minDyn: 0.75 },
};

export class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: false, premultipliedAlpha: false });
    if (!gl) throw new Error('WebGL2 is not supported on this device.');
    this.gl = gl;
    // the GPU can drop the context (driver reset, memory pressure, a long time in a background tab).
    // Stop drawing while it's gone; when the browser gives it back, rebuild everything on the GPU.
    this.lost = false;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault(); // lets the browser restore it
      this.lost = true;
    });
    canvas.addEventListener('webglcontextrestored', () => {
      this._initGL();
      for (const m of Mesh.all) m._upload();
      this.lost = false;
    });
    this._initGL();
    this.time = 0;
    this.quality = 'high';
    this.q = QUALITY.high;
    this.w = this.h = 0;
    this.shadowMat = mat4.create();
    this._lightView = mat4.create();
    this._lightProj = mat4.create();
    this._reflView = mat4.create();
    this._reflVP = mat4.create();
    this._tmpInv = mat4.create();
    this.stats = { draws: 0 };
    this.dynScale = 1;
    this._cw = window.innerWidth;
    this._ch = window.innerHeight;
    window.addEventListener('resize', () => {
      this._cw = window.innerWidth;
      this._ch = window.innerHeight;
    });
    this._lp = new Float32Array(S.MAX_LIGHTS * 4);
    this._lc = new Float32Array(S.MAX_LIGHTS * 4);
    this._ld = new Float32Array(S.MAX_LIGHTS * 4);
    this.setQuality('high');
  }
  /** Everything that lives on the GPU: shaders, buffers, render targets (again after a context loss). */
  _initGL() {
    const gl = this.gl;
    this.floatOK = !!gl.getExtension('EXT_color_buffer_float');
    gl.getExtension('OES_texture_float_linear');
    this.hdrFormat = this.floatOK ? gl.RGBA16F : gl.RGBA8;
    this.hdrType = this.floatOK ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE;
    this.maxSamples = gl.getParameter(gl.MAX_SAMPLES) || 0;
    this.progStd = compile(gl, S.standardVS, S.standardFS, 'standard');
    this.progDepth = compile(gl, S.depthVS, S.depthFS, 'depth');
    this.progFx = compile(gl, S.standardVS, S.fxFS, 'fx');
    this.progSky = compile(gl, S.fsVS, S.skyFS, 'sky');
    this.progSkyFar = compile(gl, S.skyVS, S.skyFS, 'skyfar');
    this.progPart = compile(gl, S.particleVS, S.particleFS, 'particles');
    this.progPre = compile(gl, S.fsVS, S.prefilterFS, 'prefilter');
    this.progDown = compile(gl, S.fsVS, S.downFS, 'down');
    this.progUp = compile(gl, S.fsVS, S.upFS, 'up');
    this.progComp = compile(gl, S.fsVS, S.compositeFS, 'composite');
    this.progFxaa = compile(gl, S.fsVS, S.fxaaFS, 'fxaa');
    this.progCopy = compile(gl, S.fsVS, S.copyFS, 'copy');
    this.emptyVao = gl.createVertexArray();
    this._initParticles();
    this.envTex = null;
    this.envTarget = null;
    this.envSkyKey = '';
    // 1x1 dummy textures
    this.dummyShadow = this._makeShadowTex(1);
    this.dummyTex = this._tex(1, 1, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR);
    // render targets: made afresh (setQuality builds the shadow map, the next frame the rest)
    this.shadow = null;
    this.hdr = this.ldr = this.refl = null;
    this._snap = null;
    this.bloom = [];
    this.w = this.h = 0;
    if (this.quality) this.setQuality(this.quality);
  }
  /** Dynamic resolution: called with the measured frame time to keep input responsive. */
  adaptResolution(frameMs) {
    this._ftEma = this._ftEma === undefined ? frameMs : this._ftEma * 0.9 + frameMs * 0.1;
    this._adaptT = (this._adaptT || 0) + frameMs;
    if (this._adaptT < 700) return;
    this._adaptT = 0;
    const f = this._ftEma;
    let s = this.dynScale;
    const floor = this.q.minDyn || 0.8; // never drop low enough to look blurry
    if (f > 21 && s > floor) s = Math.max(floor, s - (f > 30 ? 0.1 : 0.05));
    else if (f < 17.5 && s < 1) s = Math.min(1, s + 0.05);
    if (Math.abs(s - this.dynScale) > 0.001) this.dynScale = s;
  }

  setQuality(name) {
    this.quality = name;
    this.q = QUALITY[name] || QUALITY.high;
    const gl = this.gl;
    if (this.shadow) {
      gl.deleteTexture(this.shadow.tex);
      gl.deleteFramebuffer(this.shadow.fbo);
    }
    this.shadow = null;
    if (this.q.shadows) {
      const tex = this._makeShadowTex(this.q.shadowSize);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, tex, 0);
      gl.drawBuffers([gl.NONE]);
      gl.readBuffer(gl.NONE);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      this.shadow = { tex, fbo, size: this.q.shadowSize };
    }
    this.w = 0; // force resize
  }

  _makeShadowTex(size) {
    const gl = this.gl;
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.DEPTH_COMPONENT24, size, size);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
    return tex;
  }

  _tex(w, h, internal, format, type, filter = this.gl.LINEAR) {
    const gl = this.gl;
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }
  _target(w, h, depth = false, fmt) {
    const gl = this.gl;
    const internal = fmt || this.hdrFormat;
    const tex = this._tex(w, h, internal, gl.RGBA, internal === gl.RGBA8 ? gl.UNSIGNED_BYTE : this.hdrType);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    let rb = null;
    if (depth) {
      rb = gl.createRenderbuffer();
      gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { tex, fbo, rb, w, h };
  }
  _freeTarget(t) {
    if (!t) return;
    const gl = this.gl;
    gl.deleteTexture(t.tex);
    gl.deleteFramebuffer(t.fbo);
    if (t.rb) gl.deleteRenderbuffer(t.rb);
    if (t.msFbo) {
      gl.deleteFramebuffer(t.msFbo);
      gl.deleteRenderbuffer(t.msColor);
      gl.deleteRenderbuffer(t.msDepth);
    }
  }

  resize() {
    // no layout reads here (they force a synchronous reflow every frame); size comes from resize events
    const dprMax = this.q.dprMax || (this.q.hidpi ? 2 : 1.25);
    const dpr = Math.min(window.devicePixelRatio || 1, dprMax) * this.q.scale * this.dynScale;
    const cw = this._cw, ch = this._ch;
    const w = Math.max(2, Math.round(cw * dpr)), h = Math.max(2, Math.round(ch * dpr));
    if (w === this.w && h === this.h) return;
    this.w = w; this.h = h;
    this.canvas.width = w; this.canvas.height = h;
    const gl = this.gl;
    for (const t of [this.hdr, this.ldr, this.refl, ...(this.bloom || [])]) this._freeTarget(t);
    this.hdr = this._target(w, h, true);
    // MSAA
    const samples = Math.min(this.q.msaa, this.maxSamples);
    if (samples > 1) {
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      const c = gl.createRenderbuffer();
      gl.bindRenderbuffer(gl.RENDERBUFFER, c);
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, this.hdrFormat, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, c);
      const d = gl.createRenderbuffer();
      gl.bindRenderbuffer(gl.RENDERBUFFER, d);
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, d);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE) {
        this.hdr.msFbo = fbo; this.hdr.msColor = c; this.hdr.msDepth = d;
      } else {
        gl.deleteFramebuffer(fbo); gl.deleteRenderbuffer(c); gl.deleteRenderbuffer(d);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    this.ldr = this._target(w, h, false, gl.RGBA8);
    this.refl = this.q.reflections ? this._target(Math.max(2, w >> 1), Math.max(2, h >> 1), true) : null;
    this.bloom = [];
    let bw = w >> 1, bh = h >> 1;
    for (let i = 0; i < this.q.bloomLevels && bw > 4 && bh > 4; i++) {
      this.bloom.push(this._target(bw, bh));
      bw >>= 1; bh >>= 1;
    }
  }

  // ------------------------------------------------------------ environment
  ensureEnv(scene) {
    const key = JSON.stringify(scene.sky);
    if (this.envTex && key === this.envSkyKey) return;
    this.envSkyKey = key;
    const gl = this.gl;
    const W = 256, H = 128;
    if (!this.envTarget) {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      const levels = Math.floor(Math.log2(W)) + 1;
      gl.texStorage2D(gl.TEXTURE_2D, levels, this.hdrFormat, W, H);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      this.envTarget = { tex, fbo, levels };
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.envTarget.fbo);
    gl.viewport(0, 0, W, H);
    gl.disable(gl.DEPTH_TEST);
    this._drawSky(scene, null, true);
    gl.bindTexture(gl.TEXTURE_2D, this.envTarget.tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.envTex = this.envTarget.tex;
    this.envLod = this.envTarget.levels - 2;
  }

  _drawSky(scene, camera, equirect, far = false) {
    const gl = this.gl, P = far ? this.progSkyFar : this.progSky, u = P.u, s = scene.sky;
    gl.useProgram(P.p);
    gl.uniform1f(u.uOctaves, equirect ? 5 : this.q.skyOctaves || 5);
    if (camera) {
      gl.uniformMatrix4fv(u.uInvViewProj, false, camera.invViewProj);
      gl.uniform3fv(u.uCamPos, camera.position);
    }
    gl.uniform1f(u.uEquirect, equirect ? 1 : 0);
    gl.uniform1f(u.uTime, equirect ? 0 : this.time);
    gl.uniform3fv(u.uNeb1, s.neb1); gl.uniform3fv(u.uNeb2, s.neb2); gl.uniform3fv(u.uNeb3, s.neb3);
    gl.uniform3fv(u.uSpaceCol, s.space);
    gl.uniform3fv(u.uStarDir, s.starDir); gl.uniform3fv(u.uStarCol, s.starCol);
    gl.uniform3fv(u.uPlanetDir, s.planetDir); gl.uniform1f(u.uPlanetSize, s.planetSize);
    gl.uniform3fv(u.uPlanetCol, s.planetCol); gl.uniform3fv(u.uPlanetCol2, s.planetCol2);
    gl.uniform1f(u.uRing, s.ring); gl.uniform1f(u.uNebIntensity, s.nebIntensity); gl.uniform1f(u.uSeed, s.seed);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  // ------------------------------------------------------------ particles
  _initParticles() {
    const gl = this.gl;
    this.partVao = gl.createVertexArray();
    gl.bindVertexArray(this.partVao);
    const q = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, q);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.partBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.partBuf);
    gl.bufferData(gl.ARRAY_BUFFER, 4000 * 10 * 4, gl.DYNAMIC_DRAW);
    const st = 40;
    gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 4, gl.FLOAT, false, st, 0); gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2); gl.vertexAttribPointer(2, 4, gl.FLOAT, false, st, 16); gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3); gl.vertexAttribPointer(3, 2, gl.FLOAT, false, st, 32); gl.vertexAttribDivisor(3, 1);
    gl.bindVertexArray(null);
  }

  _drawParticles(scene, camera) {
    const gl = this.gl, P = this.progPart;
    gl.useProgram(P.p);
    gl.uniformMatrix4fv(P.u.uView, false, camera.view);
    gl.uniformMatrix4fv(P.u.uProj, false, camera.proj);
    gl.bindVertexArray(this.partVao);
    gl.depthMask(false);
    for (const ps of scene.particles) {
      const n = ps.fill();
      if (!n) continue;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.partBuf);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, ps.inst, 0, Math.min(n, 4000) * 10);
      if (ps.additive) gl.blendFunc(gl.ONE, gl.ONE);
      else gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.uniform1f(P.u.uAdditive, ps.additive ? 1 : 0);
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, Math.min(n, 4000));
    }
    gl.depthMask(true);
  }

  // ------------------------------------------------------------ main render
  _collect(scene) {
    const opaque = [], fx = [], trans = [];
    const walk = (n) => {
      if (!n.visible) return;
      if (n.mesh) {
        if (n.fx) fx.push(n);
        else if (n.tint[3] < 0.999) trans.push(n);
        else opaque.push(n);
      }
      for (const c of n.children) walk(c);
    };
    walk(scene.root);
    return { opaque, fx, trans };
  }

  _setupLights(scene, camera) {
    const u = this.progStd.u, gl = this.gl;
    const L = scene.lights.filter((l) => l.enabled && l.intensity > 0.001);
    const cap = Math.min(S.MAX_LIGHTS, this.q.maxLights || S.MAX_LIGHTS);
    if (L.length > cap) {
      // keep the lights that matter most: priority first, then the brightest reach per distance to the view centre
      const t = camera.target;
      const score = (l) => (l.intensity * l.range * l.range) / (1 + dist2(l.position, t));
      L.sort((a, b) => (b.priority - a.priority) || (score(b) - score(a)));
      L.length = cap;
    }
    const pos = this._lp, colr = this._lc, dir = this._ld;
    L.forEach((l, i) => {
      pos.set([l.position[0], l.position[1], l.position[2], l.range], i * 4);
      colr.set([l.color[0] * l.intensity, l.color[1] * l.intensity, l.color[2] * l.intensity, l.dir ? Math.cos(l.cone) : -2], i * 4);
      const d = l.dir || [0, -1, 0];
      dir.set([d[0], d[1], d[2], Math.max(0.001, Math.cos(l.cone * (1 - l.soft)) - Math.cos(l.cone))], i * 4);
    });
    gl.uniform1i(u.uNumLights, L.length);
    gl.uniform4fv(u.uLPos, pos);
    gl.uniform4fv(u.uLCol, colr);
    gl.uniform4fv(u.uLDir, dir);
  }

  _drawList(list, prog, viewProj, fx = false) {
    const gl = this.gl, u = prog.u;
    for (const n of list) {
      gl.uniformMatrix4fv(u.uModel, false, n.world);
      if (u.uTint) gl.uniform4fv(u.uTint, n.tint);
      if (!fx && u.uEmissive) {
        gl.uniform3fv(u.uEmissive, n.emissive);
        gl.uniform1f(u.uEmitMul, n.emitMul);
        if (u.uBeam) gl.uniform4fv(u.uBeam, n.beam || NO_BEAM);
      }
      if (fx) {
        gl.uniform1f(u.uFxMode, n.fx.mode || 0);
        gl.uniform1f(u.uFxParam, n.fx.param || 1);
      }
      gl.bindVertexArray(n.mesh.vao);
      gl.drawElements(gl.TRIANGLES, n.mesh.count, gl.UNSIGNED_INT, 0);
      this.stats.draws++;
    }
  }

  _stdUniforms(scene, camera, viewProj, camPos, reflOn) {
    const gl = this.gl, P = this.progStd, u = P.u, e = scene.env;
    gl.useProgram(P.p);
    gl.uniformMatrix4fv(u.uViewProj, false, viewProj);
    gl.uniformMatrix4fv(u.uShadowMat, false, this.shadowMat);
    gl.uniform3fv(u.uCamPos, camPos);
    const sd = normalize3(e.sunDir);
    gl.uniform3fv(u.uSunDir, sd);
    gl.uniform3fv(u.uSunColor, e.sunColor.map((c) => c * e.sunIntensity));
    gl.uniform3fv(u.uSkyAmb, e.skyAmb);
    gl.uniform3fv(u.uGroundAmb, e.groundAmb);
    gl.uniform3fv(u.uRimColor, e.rim.map((c) => c * e.rimIntensity));
    gl.uniform3fv(u.uFogColor, e.fogColor);
    gl.uniform1f(u.uFogDensity, e.fogDensity);
    gl.uniform1f(u.uFogHeight, e.fogHeight);
    gl.uniform1f(u.uEnvIntensity, e.envIntensity);
    gl.uniform1f(u.uEnvLod, this.envLod || 6);
    gl.uniform1f(u.uLdr, 0);
    gl.uniform1f(u.uTime, this.time);
    gl.uniform2f(u.uViewport, this.w, this.h);
    this._setupLights(scene, camera);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.shadow && this.q.shadows ? this.shadow.tex : this.dummyShadow);
    gl.uniform1i(u.uShadowMap, 0);
    gl.uniform1f(u.uShadowOn, this.shadow && this.q.shadows ? 1 : 0);
    gl.uniform1f(u.uShadowTexel, this.shadow ? 1 / this.shadow.size : 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.envTex);
    gl.uniform1i(u.uEnvMap, 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, reflOn && this.refl ? this.refl.tex : this.dummyTex);
    gl.uniform1i(u.uReflTex, 2);
    gl.uniform1f(u.uReflOn, reflOn ? 1 : 0);
  }

  _renderScenePass(scene, camera, lists, viewProj, camPos, opts) {
    const gl = this.gl;
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(opts.flip ? gl.CW : gl.CCW);
    this._stdUniforms(scene, camera, viewProj, camPos, opts.reflOn);
    gl.uniform1f(this.progStd.u.uClipY, opts.clipY);
    gl.disable(gl.BLEND);
    this._drawList(lists.opaque, this.progStd, viewProj);
    // sky after the opaque geometry, at the far plane: only uncovered pixels pay for the nebula shader
    if (scene.skyVisible) {
      gl.depthMask(false);
      gl.disable(gl.CULL_FACE);
      this._drawSky(scene, opts.skyCam || camera, false, true);
      gl.enable(gl.CULL_FACE);
      gl.depthMask(true);
      gl.useProgram(this.progStd.p);
    }
    // translucent standard (fading items)
    if (lists.trans.length) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      this._drawList(lists.trans, this.progStd, viewProj);
      gl.depthMask(true);
    }
    // additive effects
    if (lists.fx.length) {
      const P = this.progFx;
      gl.useProgram(P.p);
      gl.uniformMatrix4fv(P.u.uViewProj, false, viewProj);
      gl.uniform3fv(P.u.uCamPos, camPos);
      gl.uniform1f(P.u.uTime, this.time);
      gl.uniform1f(P.u.uClipY, opts.clipY);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      gl.depthMask(false);
      gl.disable(gl.CULL_FACE);
      this._drawList(lists.fx, P, viewProj, true);
      gl.depthMask(true);
    }
    if (!opts.noParticles) {
      gl.enable(gl.BLEND);
      this._drawParticles(scene, opts.partCam || camera);
    }
    gl.disable(gl.BLEND);
    gl.frontFace(gl.CCW);
  }

  _fsPass(prog, target, setup) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fbo : null);
    gl.viewport(0, 0, target ? target.w : this.w, target ? target.h : this.h);
    gl.useProgram(prog.p);
    setup(prog.u);
    gl.bindVertexArray(this.emptyVao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  _bindTex(unit, tex, loc) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(loc, unit);
  }

  render(scene, camera, dt = 0.016) {
    const gl = this.gl;
    if (this.lost) return; // waiting for the GPU to come back
    this.time += dt;
    this.stats.draws = 0;
    this.resize();
    camera.update(this.w / this.h);
    scene.root.updateWorld(null);
    this.ensureEnv(scene);
    const lists = this._collect(scene);
    // draw opaque geometry front-to-back so the depth test rejects hidden pixels before the expensive lighting runs
    {
      const cp = camera.position;
      for (const n of lists.opaque) {
        const w = n.world, dx = w[12] - cp[0], dy = w[13] - cp[1], dz = w[14] - cp[2];
        n._d2 = dx * dx + dy * dy + dz * dz;
      }
      lists.opaque.sort((a, b) => a._d2 - b._d2);
    }
    const e = scene.env;

    // --- shadow pass
    if (this.shadow && this.q.shadows) {
      const sd = normalize3(e.sunDir);
      const c = e.shadowCenter, R = e.shadowRadius;
      const eye = [c[0] - sd[0] * R * 2, c[1] - sd[1] * R * 2, c[2] - sd[2] * R * 2];
      mat4.lookAt(this._lightView, eye, c, Math.abs(sd[1]) > 0.99 ? [0, 0, 1] : [0, 1, 0]);
      mat4.ortho(this._lightProj, -R, R, -R, R, 0.1, R * 4);
      const lvp = mat4.multiply(mat4.create(), this._lightProj, this._lightView);
      this.shadowMat.set(lvp);
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.shadow.fbo);
      gl.viewport(0, 0, this.shadow.size, this.shadow.size);
      gl.clear(gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.cullFace(gl.FRONT);
      gl.enable(gl.POLYGON_OFFSET_FILL);
      gl.polygonOffset(1.5, 3);
      const P = this.progDepth;
      gl.useProgram(P.p);
      gl.uniformMatrix4fv(P.u.uViewProj, false, lvp);
      this._drawList(lists.opaque.filter((n) => n.castShadow), P, lvp, false);
      gl.disable(gl.POLYGON_OFFSET_FILL);
      gl.cullFace(gl.BACK);
    }

    // --- planar reflection pass (mirror about y=0)
    const reflOn = !!(this.refl && e.reflections && this.q.reflections);
    if (reflOn) {
      const mirror = mat4.create();
      mirror[5] = -1;
      mat4.multiply(this._reflView, camera.view, mirror);
      mat4.multiply(this._reflVP, camera.proj, this._reflView);
      const rc = { ...camera, position: [camera.position[0], -camera.position[1], camera.position[2]], view: this._reflView, proj: camera.proj, viewProj: this._reflVP, invViewProj: mat4.invert(mat4.create(), this._reflVP), target: camera.target };
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.refl.fbo);
      gl.viewport(0, 0, this.refl.w, this.refl.h);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      const rl = { opaque: lists.opaque.filter((n) => n.reflect), trans: [], fx: lists.fx.filter((n) => n.reflect) };
      const savedW = this.w, savedH = this.h;
      this._renderScenePass(scene, rc, rl, this._reflVP, rc.position, { flip: true, reflOn: false, clipY: 0.02, skyCam: rc, noParticles: true, partCam: rc });
      this.w = savedW; this.h = savedH;
    }

    // --- main pass
    const ms = this.hdr.msFbo;
    gl.bindFramebuffer(gl.FRAMEBUFFER, ms || this.hdr.fbo);
    gl.viewport(0, 0, this.w, this.h);
    const fc = e.fogColor;
    gl.clearColor(fc[0], fc[1], fc[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    this._renderScenePass(scene, camera, lists, camera.viewProj, camera.position, { flip: false, reflOn, clipY: e.clipY });
    if (ms) {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, ms);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.hdr.fbo);
      gl.blitFramebuffer(0, 0, this.w, this.h, 0, 0, this.w, this.h, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);

    // --- bloom
    const B = this.bloom;
    if (B.length && this.q.bloom) {
      this._fsPass(this.progPre, B[0], (u) => {
        this._bindTex(0, this.hdr.tex, u.uTex);
        gl.uniform2f(u.uTexel, 1 / this.w, 1 / this.h);
        gl.uniform1f(u.uThreshold, e.bloomThreshold);
      });
      for (let i = 1; i < B.length; i++) {
        this._fsPass(this.progDown, B[i], (u) => {
          this._bindTex(0, B[i - 1].tex, u.uTex);
          gl.uniform2f(u.uTexel, 1 / B[i - 1].w, 1 / B[i - 1].h);
        });
      }
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      for (let i = B.length - 1; i > 0; i--) {
        this._fsPass(this.progUp, B[i - 1], (u) => {
          this._bindTex(0, B[i].tex, u.uTex);
          gl.uniform2f(u.uTexel, 1 / B[i].w, 1 / B[i].h);
          gl.uniform1f(u.uWeight, 1.0);
        });
      }
      gl.disable(gl.BLEND);
    }

    // --- composite
    const useFxaa = !ms;
    this._fsPass(this.progComp, useFxaa ? this.ldr : null, (u) => {
      this._bindTex(0, this.hdr.tex, u.uHdr);
      this._bindTex(1, B.length && this.q.bloom ? B[0].tex : this.dummyTex, u.uBloom);
      gl.uniform1f(u.uBloomStrength, B.length && this.q.bloom ? e.bloom * 0.12 : 0);
      gl.uniform1f(u.uExposure, e.exposure);
      gl.uniform1f(u.uTime, this.time);
      gl.uniform1f(u.uVignette, e.vignette);
      gl.uniform1f(u.uGrain, e.grain);
      gl.uniform1f(u.uAberration, e.aberration);
      gl.uniform1f(u.uSaturation, e.saturation);
      gl.uniform3fv(u.uLift, e.lift);
      gl.uniform3fv(u.uGain, e.gain);
      gl.uniform1f(u.uFlash, scene.flash);
      gl.uniform3fv(u.uFlashColor, scene.flashColor);
      gl.uniform1f(u.uDesat, scene.desat);
    });
    if (useFxaa) {
      this._fsPass(this.progFxaa, null, (u) => {
        this._bindTex(0, this.ldr.tex, u.uTex);
        gl.uniform2f(u.uTexel, 1 / this.w, 1 / this.h);
      });
    }
  }

  /** Render a small list of nodes into an image (used for UI icons). */
  snapshot(nodes, camera, size, scene) {
    const gl = this.gl;
    if (!this._snap || this._snap.w !== size) {
      this._freeTarget(this._snap);
      this._snap = this._target(size, size, true, gl.RGBA8);
    }
    const t = this._snap;
    this.ensureEnv(scene);
    camera.update(1);
    const root = new Node();
    for (const n of nodes) root.children.push(n);
    root.updateWorld(null);
    const list = [];
    root.traverse((n) => n.visible && n.mesh && !n.fx && list.push(n));
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
    gl.viewport(0, 0, size, size);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.disable(gl.BLEND);
    const savedShadow = this.q.shadows;
    this.q.shadows = false;
    this._stdUniforms(scene, camera, camera.viewProj, camera.position, false);
    this.q.shadows = savedShadow;
    gl.uniform1f(this.progStd.u.uLdr, 1);
    gl.uniform1f(this.progStd.u.uClipY, -1e9);
    gl.uniform1f(this.progStd.u.uFogDensity, 0);
    this._drawList(list, this.progStd, camera.viewProj);
    const px = new Uint8Array(size * size * 4);
    gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, px);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) img.data.set(px.subarray((size - 1 - y) * size * 4, (size - y) * size * 4), y * size * 4);
    ctx.putImageData(img, 0, 0);
    return c.toDataURL('image/png');
  }
}

function normalize3(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}
function dist2(a, b) {
  return (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;
}
